import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MessageSquare, Phone, Mail, X, Pin, PinOff, Trash2, Search,
  ChevronDown, ChevronUp, Send, StickyNote
} from 'lucide-react';
import { getBackendUrl } from '../utils/getBackendUrl';

const API = `${getBackendUrl()}/api`;

const TYPE_META = {
  sms:   { label: 'Text',  icon: MessageSquare, color: 'bg-blue-500 text-white',    border: 'border-blue-200',    bg: 'bg-blue-50' },
  call:  { label: 'Call',  icon: Phone,         color: 'bg-emerald-500 text-white', border: 'border-emerald-200', bg: 'bg-emerald-50' },
  email: { label: 'Email', icon: Mail,          color: 'bg-orange-500 text-white',  border: 'border-orange-200',  bg: 'bg-orange-50' },
  note:  { label: 'Note',  icon: StickyNote,    color: 'bg-gray-700 text-white',    border: 'border-gray-200',    bg: 'bg-gray-50' },
};

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-CA', { timeZone: 'America/Toronto', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/**
 * MessageLogDialog
 * Shows the conversation/communication log between the current user and `contact`.
 * Lets the user add a new log entry (note) and surfaces pin/read/delete controls.
 *
 * Props:
 *   contact: { id, name, phone, email }
 *   currentUserId: string
 *   onClose: () => void
 */
export default function MessageLogDialog({ contact, currentUserId, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newType, setNewType] = useState('note');
  const [busy, setBusy] = useState(false);
  const fetchedOnce = useRef(false);

  const fetchItems = async () => {
    try {
      const { data } = await axios.get(`${API}/communications`, {
        params: { with_user_id: contact.id, ...(typeFilter ? { type: typeFilter } : {}), ...(search ? { search } : {}) },
      });
      setItems(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load message log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchItems();
    // Mark conversation as read on open (one-shot)
    if (!fetchedOnce.current) {
      fetchedOnce.current = true;
      axios.put(`${API}/communications/conversation/${contact.id}/read-all`).catch(() => {});
    }
    // eslint-disable-next-line
  }, [contact?.id, typeFilter]);

  // Debounced re-fetch on search change
  useEffect(() => {
    const t = setTimeout(() => fetchItems(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const handleAdd = async () => {
    if (!newContent.trim()) {
      toast.error('Type something first');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/communications`, {
        to_user_id: contact.id,
        type: newType,
        content: newContent.trim(),
        direction: 'outgoing',
      });
      setNewContent('');
      await fetchItems();
      toast.success('Saved to log');
    } catch {
      toast.error('Failed to save');
    } finally {
      setBusy(false);
    }
  };

  const togglePin = async (id) => {
    try {
      await axios.put(`${API}/communications/${id}/pin`);
      await fetchItems();
    } catch {
      toast.error('Failed to pin');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this log entry?')) return;
    try {
      await axios.delete(`${API}/communications/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch {
      toast.error("Can't delete (only the sender can remove their own entries)");
    }
  };

  const pinned = items.filter((i) => i.is_pinned);
  const rest = items.filter((i) => !i.is_pinned);

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/60 flex items-end sm:items-center justify-center p-2 sm:p-4"
      onClick={onClose}
      data-testid="message-log-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b bg-gradient-to-r from-blue-600 to-blue-700 text-white flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide opacity-80">Message Log</p>
            <h3 className="font-bold truncate">{contact.name || 'Contact'}</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/20" data-testid="close-message-log">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-4 py-2 border-b bg-gray-50 space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search messages…"
              className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              data-testid="message-search"
            />
          </div>
          <div className="flex gap-1.5 overflow-x-auto">
            {['', 'sms', 'call', 'email', 'note'].map((t) => (
              <button
                key={t || 'all'}
                onClick={() => setTypeFilter(t)}
                className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                  typeFilter === t ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600'
                }`}
                data-testid={`filter-${t || 'all'}`}
              >
                {t ? TYPE_META[t]?.label : 'All'}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {loading ? (
            <p className="text-center text-sm text-gray-500 py-10">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-center text-sm text-gray-500 py-10">No messages yet. Send a text, make a call, or jot a note — it'll be saved here.</p>
          ) : (
            <>
              {pinned.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[11px] uppercase font-bold text-gray-400 tracking-wide">Pinned</p>
                  {pinned.map((it) => <LogItem key={it.id} item={it} currentUserId={currentUserId} onPin={togglePin} onDelete={remove} />)}
                </div>
              )}
              {pinned.length > 0 && rest.length > 0 && <div className="h-px bg-gray-100 my-2" />}
              {rest.map((it) => <LogItem key={it.id} item={it} currentUserId={currentUserId} onPin={togglePin} onDelete={remove} />)}
            </>
          )}
        </div>

        {/* Composer */}
        <div className="border-t bg-white p-3 space-y-2">
          <div className="flex gap-1.5">
            {['note', 'sms', 'call', 'email'].map((t) => {
              const M = TYPE_META[t];
              const Icon = M.icon;
              return (
                <button
                  key={t}
                  onClick={() => setNewType(t)}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                    newType === t ? M.color : 'bg-gray-100 text-gray-600'
                  }`}
                  data-testid={`new-type-${t}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {M.label}
                </button>
              );
            })}
          </div>
          <div className="flex gap-2">
            <textarea
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              placeholder={`Log a ${TYPE_META[newType]?.label.toLowerCase() || 'note'}…`}
              rows={2}
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
              data-testid="message-input"
            />
            <button
              onClick={handleAdd}
              disabled={busy || !newContent.trim()}
              className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-200 disabled:text-gray-400 text-sm font-bold flex items-center gap-1"
              data-testid="send-message-btn"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LogItem({ item, currentUserId, onPin, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const meta = TYPE_META[item.type] || TYPE_META.note;
  const Icon = meta.icon;
  const mine = item.from_user_id === currentUserId;
  const long = (item.content || '').length > 220;
  const preview = long && !expanded ? item.content.slice(0, 220) + '…' : item.content;

  return (
    <div
      className={`rounded-xl border ${meta.border} ${meta.bg} p-3 flex items-start gap-3`}
      data-testid={`log-item-${item.id}`}
    >
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${meta.color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-xs font-bold text-gray-900">
            {mine ? `You → ${item.to_name}` : `${item.from_name} → You`}
          </span>
          <span className="text-[10px] text-gray-500">{timeAgo(item.created_at)}</span>
        </div>
        {item.subject && (
          <p className="text-xs font-semibold text-gray-800 mb-0.5">{item.subject}</p>
        )}
        {item.content && (
          <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{preview}</p>
        )}
        {long && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-[11px] text-blue-600 hover:underline mt-1 flex items-center gap-0.5"
          >
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {expanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
      <div className="flex flex-col items-end gap-1">
        <button
          onClick={() => onPin(item.id)}
          className={`p-1 rounded hover:bg-white ${item.is_pinned ? 'text-amber-600' : 'text-gray-400'}`}
          title={item.is_pinned ? 'Unpin' : 'Pin'}
          data-testid={`pin-${item.id}`}
        >
          {item.is_pinned ? <Pin className="w-4 h-4 fill-current" /> : <PinOff className="w-4 h-4" />}
        </button>
        {mine && (
          <button
            onClick={() => onDelete(item.id)}
            className="p-1 rounded hover:bg-white text-gray-400 hover:text-red-600"
            title="Delete"
            data-testid={`delete-${item.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
